/* eslint-disable jsx-a11y/alt-text */
import { Outlet, useNavigate } from "react-router-dom";
import React, { memo, useState, useEffect, useRef } from "react";
import { sidebarItems } from "./sideBarItems";
import { createPortal } from "react-dom"; //used to fix profile dropdown popup is being covered by the table header in all module
import { decodeToken } from "utility/JwtUtility";
import authManager from "services/auth/AuthManager";
import Sidebar from "components/sidebar";

import Logo from "assets/team_pulse_logo.svg";
import { signOut } from "firebase/auth";
import { auth } from "../components/firebase/firebase";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import GenericModal from "components/Modal";
import HandleUpdatePassword from "./UpdatePasswordForm";

const AdminLayouts = memo(() => {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });
  const [isUpdatePasswordModalOpen, setIsUpdatePasswordModalOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const token = authManager.getAccessToken();
  const user = token ? decodeToken(token) : "";

  if (user?.sub) {
    localStorage.setItem("userName", user.email);
  }

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log("User logged out successfully!");

      // Clear local storage
      localStorage.removeItem("accessToken");

      // Redirect to login page
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleUpdatePassword = () => {
    setShowProfileDropdown(false);
    setIsUpdatePasswordModalOpen(true);
  };

  const handleLogoutClick = () => {
    setShowProfileDropdown(false);
    setShowLogoutConfirm(true);
  };

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar
        items={sidebarItems}
        logo={Logo}
        appName="Team Pulse"
        userName={user?.email ?? "Admin"}
        userAvatar={Logo}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
          <div className="flex items-center justify-end h-16 px-6">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 mx-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              aria-label={darkMode ? "Swit ref={profileDropdownRef}ch to light mode" : "Switch to dark mode"}
            >
              <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
            </button>


            {/* User Profile */}
            <div className="relative ml-4">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center focus:outline-none"
              >
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-blue-500 dark:border-gray-600 hover:border-blue-700 dark:hover:border-blue-600 transition-colors cursor-pointer">
                  {/* <img src={Logo} className="h-full w-full object-cover" alt="Profile" /> */}
                  <p className="h-full w-full flex items-center justify-center bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold">
                    {user?.email?.charAt(0).toUpperCase() ?? "A"}
                  </p>
                </div>
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    className="fixed top-[70px] right-6 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[9999]"
                  >
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        {user?.email ?? "Admin"}
                      </p>

                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {user?.role ?? "Administrator"}
                      </p>
                    </div>

                    <div className="p-2">
                      <button
                        onClick={handleUpdatePassword}
                        className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
                        Update Password
                      </button>

                      <button
                        onClick={handleLogoutClick}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center"
                      >
                        <FontAwesomeIcon icon={faPowerOff} className="mr-2" />
                        Logout
                      </button>
                    </div>
                  </div>,
                  document.body
                )
              }
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 bg-gray-50 dark:bg-gray-900">
          <Outlet />
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4 transform transition-all">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
              Confirm Logout
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to logout? Any unsaved changes will be lost.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
      {
        isUpdatePasswordModalOpen && (
          <GenericModal
            isOpen={isUpdatePasswordModalOpen}
            onClose={() => setIsUpdatePasswordModalOpen(false)}
            header="Update password"
            customWidth="md:w-96"
          >
            <HandleUpdatePassword onClose={() => setIsUpdatePasswordModalOpen(false)} />
          </GenericModal>
        )
      }
    </div>
  );
});

export default AdminLayouts;
